import React, { useEffect, useState } from "react";
import {
  Input,
  Label,
  Button,
  FormFeedback,
  Col,
  Row,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Alert,
} from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { NAME_MAXLENGTH } from "@constants/constants";
import Spinners from "@components/custom-table/Spinner";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { stateSchema } from "@helpers/validation-schema";
import { PHONE_NUMBER_MAXLENGTH } from "@constants/constants";
import { resetError, resetState } from "../controller/content-slice";
import {
  editContent,
  getContent,
  getContentCount,
} from "../services/content-services";
import { Client } from "@helpers/helpers";
import { API_METHOD } from "@constants/constants";
import { url } from "@constants/url-constants";
import ViewModal from "../../../components/custom-table/ViewModel";
import Switch from "react-switch";
import { Offsymbol, OnSymbol } from "../../../helpers/helpers";
import CustomLabel from "../../../components/CustomLabel";
import { contentSchema } from "../../../helpers/validation-schema";
import { getPresignedUrl } from "../../media/services/media-services";

export const AddContent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const route = pathname.split("/")[2];
  const contentId = pathname.split("/")[3];
  const success = useSelector((state) => state.content.success);
  const content = useSelector((state) => state.content.contents);
  const loading = useSelector((state) => state.content.loading);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageQuery = parseInt(searchParams.get("current_page"));
  const contentPage = parseInt(searchParams.get("content_page"));
  const pageId = searchParams.get("id");
  const successMessage = useSelector((state) => state.content.successMessage);
  const errorMessage = useSelector((state) => state.content.errorMessage);
  const [duration, setDuration] = useState(null);
  const [trailerType, setTrailerType] = useState("");
  const [uploadError, setUploadError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [viewModel, setViewModel] = useState(false);
  const [viewImageUrl, setViewImageUrl] = useState("");
  const [mediaInfo, setMediaInfo] = useState({
    size: 0,
    mime_type: "",
    width: null,
    height: null,
  });
  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(contentSchema),
  });

  useEffect(() => {
    if (route === "add") {
      async function fetchCount() {
        const count = await getContentCount();
        setValue("sorting", count + 1);
      }
      fetchCount();
    }
  }, [setValue]);

  useEffect(() => {
    if (route !== "add") {
      dispatch(getContent(contentId));
    }
  }, [contentId, dispatch, route]);

  useEffect(() => {
    if (success) {
      if (pageId) {
        navigate(
          `/medias/edit/${pageId}?current_page=${pageQuery}&&content_page=${contentPage}`
        );
        dispatch(resetState());
      }
    }
  }, [success, dispatch, navigate, successMessage, contentPage, pageId]);

  useEffect(() => {
    if (errorMessage !== null) {
      toast.error(errorMessage?.message);

      errorMessage.data.items.forEach((item) => {
        const fieldName = Object.keys(item)[0];
        const message = item[fieldName];
        setError(fieldName, { type: "manual", message });
      });
    }
  }, [errorMessage, setError]);

  useEffect(() => {
    if (content) {
      setValue("name", content?.name);
      setValue("sorting", content?.sorting);
      setValue("media", content?.media);
      setValue("duration", content?.duration);
      setValue("media_type", content?.media_type);
      setValue("is_free", content?.is_free);
    }
  }, [content, setValue]);
  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      let tempUrl = "";

      if (data.mediaUpload) {
        const generatePresignedUrl = await handleMediaUpload(data.mediaUpload);
        if (generatePresignedUrl.imageTemp) {
          tempUrl = generatePresignedUrl.imageTemp;
        } else {
          setIsLoading(false);
        }
      }

      if (route === "edit") {
        const formattedData = {
          ...data,
          sorting: parseInt(data.sorting),
          duration: duration !== null ? duration : content?.duration,
          media: !tempUrl ? content?.media : { url: tempUrl, ...mediaInfo },
          media_type: trailerType ? trailerType : content?.media_type,
        };

        saveContentForm(formattedData, contentId);
      }
    } catch (error) {
      toast.error("Error during form submission:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveContentForm = (formattedData, contentId = "") => {
    if (contentId) {
      dispatch(editContent({ id: contentId, data: formattedData }));
    }
  };
  const handleMediaUpload = async (file) => {
    const fileExtension = {
      extensions: [file?.name.split(".").pop()?.toLowerCase()],
    };

    try {
      const response = await Client(
        API_METHOD.POST,
        url.presigned_url,
        fileExtension
      );
      const presignedUrl = response.data.data.items[0].preSignedUrl;

      const imageTemp = response.data.data.items[0].fileName;

      const saveImage = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        redirect: "follow",
      });
      if (saveImage.status === 200) {
        return { imageTemp };
      } else {
        return { type: "error", message: "Failed to Upload Image" };
      }
    } catch (error) {
      return { type: "error", message: "Failed to Upload Image" };
    }
  };

  const handleInputChange = (event, field) => {
    const { value } = event.target;
    const formattedValue = value.replace(/\D/g, "");
    setValue(field, formattedValue);
  };
  const handleFileChange = (file, onChange) => {

    if (!file) return;

    const url = URL.createObjectURL(file);
    const isVideo = file.type.startsWith("video");
    const mediaElement = document.createElement(isVideo ? "video" : "audio");

    const maxDurationInSeconds = isVideo ? 600 : 7200;
    const maxFileSizeInBytes = isVideo ? 1000 * 1024 * 1024 : 200 * 1024 * 1024;

    if (file.size > maxFileSizeInBytes) {
      onChange(null);
      setUploadError(
        `The uploaded file exceeds the maximum size of ${isVideo ? "1000 MB" : "200 MB"
        }.`
      );


      return;
    }

    if (file.type.split("/")[1] === "mp4") {
      setTrailerType(2);
    } else {
      setTrailerType(1);
    }

    mediaElement.src = url;
    mediaElement.onloadedmetadata = () => {
      const durationInSeconds = Math.floor(mediaElement.duration);

      setMediaInfo({
        size: file.size,
        mime_type: file.type,
        width: mediaElement.videoWidth || null,
        height: mediaElement.videoHeight || null,
      });

      if (durationInSeconds > maxDurationInSeconds) {
        setUploadError(
          `The uploaded file exceeds the maximum duration of ${isVideo ? "10 minutes" : "120 minutes"
          }.`
        );
        setDuration(null);
        onChange(null);
      } else {
        setUploadError(null);
        setDuration(durationInSeconds);
        onChange(file);
      }

      URL.revokeObjectURL(url); // Clean up the URL object
    };

    mediaElement.onerror = () => {
      console.error("Error loading media file");
      setUploadError("Error loading media file.");
      URL.revokeObjectURL(url); // Clean up the URL object
    };
  };



  const handleViewImage = async (imageUrl) => {
    const response = await dispatch(getPresignedUrl(imageUrl)).unwrap();
    setViewImageUrl(response.data.item.download_url);
    setViewModel(true);
  };

  return (
    <div className="page-content">
      {loading ? (
        <Spinners />
      ) : (
        <>
          {viewModel && (
            <ViewModal
              show={viewModel}
              onCloseClick={() => setViewModel(false)}
              data={viewImageUrl}
            />
          )}

          <ToastContainer autoClose={2000} />
          <Card>
            <CardBody className="text-start">
              <CardTitle className="mb-4 my-3">
                {route === "edit"
                  ? "Edit Episode/Chapter "
                  : route === "view"
                    ? "View State"
                    : "Create State"}
              </CardTitle>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Sorting" isRequired>
                        Sorting
                      </CustomLabel>
                      <Controller
                        name="sorting"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Sorting"
                              invalid={!!errors.sorting}
                              maxLength={PHONE_NUMBER_MAXLENGTH}
                              onChange={(e) => handleInputChange(e, "sorting")}
                            />
                            {errors.sorting && (
                              <FormFeedback>
                                {errors.sorting.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Name" isRequired>
                        Name
                      </CustomLabel>
                      <Controller
                        name="name"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Enter content name"
                              invalid={!!errors.name}
                              // value={country?.name??""}
                              maxLength={NAME_MAXLENGTH}
                            />
                            {errors.name && (
                              <FormFeedback>{errors.name.message}</FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    <CustomLabel htmlFor="Media File" isRequired>
                      Media File
                    </CustomLabel>

                    {route !== "add" ? (
                      <button
                        type="button"
                        className="view-button"
                        onClick={() => {
                          handleViewImage(content?._id);
                        }}
                      >
                        View
                      </button>
                    ) : null}

                    <Controller
                      name="mediaUpload"
                      control={control}
                      defaultValue={null}
                      render={({ field }) => (
                        <div>
                          <Input
                            type="file"
                            accept="video/*,audio/*"
                            onChange={(e) =>
                              handleFileChange(
                                e.target.files[0],
                                field.onChange
                              )
                            }
                            invalid={!!errors.mediaUpload}
                          />
                          {errors.mediaUpload && (
                            <FormFeedback>
                              {errors.mediaUpload.message}
                            </FormFeedback>
                          )}
                          {uploadError && (
                            <Alert color="danger">{uploadError}</Alert>
                          )}
                        </div>
                      )}
                    />
                  </Col>
                  <Col md={6}>
                    <Label for="is_free">Is free:</Label>
                    <FormGroup>
                      <Controller
                        name="is_free"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            id="isUserSwitch"
                            onChange={(checked) => field.onChange(checked)}
                            checked={field.value}
                            uncheckedIcon={<Offsymbol />}
                            checkedIcon={<OnSymbol />}
                            className="me-1 mb-sm-8 mb-2"
                            onColor="#02a499"
                          />
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}></Col>
                  <Col md={6}>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => {
                          if (pageId) {
                            navigate(
                              `/medias/edit/${pageId}?current_page=${pageQuery}&&content_page=${contentPage}`
                            );
                            dispatch(resetError());
                          }
                        }}
                      >
                        {route === "view" ? "Back" : "Cancel"}
                      </Button>
                      {route !== "view" ? (
                        <Button
                          type="submit"
                          className={isLoading ? "disabled" : ""}
                          color={isLoading ? "success" : "primary"}
                        >
                          {isLoading ? (
                            <>Loading...</>
                          ) : (
                            <>
                              {route === "edit"
                                ? "Edit Content"
                                : "Create Content"}
                            </>
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

export default AddContent;
